import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ShoppingCartIcon, MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';
import SocialBar from './SocialBar';
import CategoryNavigation from './CategoryNavigation';
import CollectionNavigation from './CollectionNavigation';
import { useCart } from '../context/CartContext';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalQuantity } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [socialBarVisible, setSocialBarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [cartBump, setCartBump] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Utility function to construct proper avatar URL
  const getAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
    if (!avatarUrl) return undefined;
    if (avatarUrl.startsWith('http')) return avatarUrl;
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://139.59.98.59'}/storage/${avatarUrl}`;
  };

  // Helper function to check if a nav item is active
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };



  // Function to check authentication status
  const checkAuthStatus = React.useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoading(true);
      apiService.getCurrentUser()
        .then(response => {
          // User data fetched successfully
          
          setUser(response.user);
          // Update localStorage with fresh user data
          localStorage.setItem('user', JSON.stringify(response.user));
        })
        .catch(error => {
          localStorage.removeItem('token');
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  // Listen for localStorage changes to update user data in real-time
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' && e.newValue) {
        try {
          const updatedUser = JSON.parse(e.newValue);
          setUser(updatedUser);
        } catch (error) {
          // Error parsing user data
        }
      }
    };

    // Listen for storage events (works across tabs)
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events within the same tab
    const handleUserUpdate = (e: CustomEvent) => {
      setUser(e.detail);
    };

    window.addEventListener('userUpdated', handleUserUpdate as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleUserUpdate as EventListener);
    };
  }, []);

  // Initial auth check
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Bump cart icon when quantity changes
  useEffect(() => {
    if (totalQuantity <= 0) return;
    setCartBump(true);
    const t = setTimeout(() => setCartBump(false), 300);
    return () => clearTimeout(t);
  }, [totalQuantity]);

  // Listen for auth state changes
  useEffect(() => {
    const handleAuthChange = () => {
      checkAuthStatus();
    };

    // Listen for custom auth events
    window.addEventListener('authStateChanged', handleAuthChange);
    
    // Also listen for storage changes (in case of logout from another tab)
    window.addEventListener('storage', handleAuthChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [checkAuthStatus]);

  useEffect(() => {
    const controlSocialBar = () => {
      if (typeof window !== 'undefined') {
        const currentScrollY = window.scrollY;
        
        // Show social bar when scrolling up or at top
        if (currentScrollY < lastScrollY || currentScrollY < 10) {
          setSocialBarVisible(true);
        } else {
          // Hide social bar when scrolling down
          setSocialBarVisible(false);
        }
        
        setLastScrollY(currentScrollY);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', controlSocialBar);
      
      return () => {
        window.removeEventListener('scroll', controlSocialBar);
      };
    }
  }, [lastScrollY]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await apiService.logout();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsProfileDropdownOpen(false);
      
      // Notify other components about auth state change
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      
      window.location.href = '/';
    } catch (error) {
      // Force logout even if API call fails
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsProfileDropdownOpen(false);
      
      // Notify other components about auth state change
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      
      window.location.href = '/';
    }
  };

  return (
    <>
      <SocialBar />
      <header className={`bg-white sticky z-40 border-b border-gray-200 transition-all duration-300 ${
        socialBarVisible ? 'md:top-10 top-0' : 'top-0'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo/logo.PNG" 
                  alt="Signature" 
                  className="h-10 w-auto"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link 
                to="/" 
                className={`transition-colors text-sm font-medium ${
                  isActiveRoute('/') 
                    ? 'text-gray-900 border-b-2 border-gray-900 pb-1' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Home
              </Link>
              <CategoryNavigation isActive={isActiveRoute('/products')} />
              <CollectionNavigation isActive={isActiveRoute('/collection')} />
              <Link 
                to="/wholesale-registration" 
                className={`transition-colors text-sm font-medium ${
                  isActiveRoute('/wholesale-registration') 
                    ? 'text-gray-900 border-b-2 border-gray-900 pb-1' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Wholesale For Shops
              </Link>
              <Link 
                to="/exchange" 
                className={`transition-colors text-sm font-medium ${
                  isActiveRoute('/exchange') 
                    ? 'text-gray-900 border-b-2 border-gray-900 pb-1' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Exchange Request
              </Link>
              <Link 
                to="/contact" 
                className={`transition-colors text-sm font-medium ${
                  isActiveRoute('/contact') 
                    ? 'text-gray-900 border-b-2 border-gray-900 pb-1' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Contact
              </Link>
              
            </nav>

            {/* Right side icons */}
            <div className="flex items-center space-x-4">
              {/* Search Icon */}
              <button 
                onClick={() => setIsSearchOpen(true)}
                className="text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="Search"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
              
              {/* Cart */}
              <Link
                to="/cart"
                className={`text-gray-600 hover:text-gray-900 transition-all relative ${cartBump ? 'scale-110' : ''}`}
              >
                <div className={`relative ${cartBump ? 'animate-pulse' : ''}`}>
                  <ShoppingCartIcon className="h-5 w-5" />
                  {totalQuantity > 0 && (
                    <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs rounded-full min-h-4 min-w-4 h-4 px-1 flex items-center justify-center text-[10px]">
                      {totalQuantity}
                    </span>
                  )}
                </div>
              </Link>

              {/* Authentication */}
              {!isLoading && (
                <div className="hidden md:flex items-center space-x-2">

                  {user ? (
                    <div className="relative" ref={profileDropdownRef}>
                      {/* Profile Avatar Button */}
                      <button
                        onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                        className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-all duration-200 focus:outline-none group"
                      >
                        <div className="relative">
                          {/* Avatar Circle with Image or Gradient */}
                          <div className="w-8 h-8 rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-all duration-200 group-hover:scale-105 ring-2 ring-white">
                            {user.avatar_url ? (
                              <img
                                src={getAvatarUrl(user.avatar_url)}
                                alt={user.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Avatar image failed to load
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                                <span className="text-white text-sm font-semibold tracking-wide">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          {/* Online Status Indicator */}
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
                        </div>
                        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Profile Dropdown */}
                      {isProfileDropdownOpen && (
                        <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-2xl py-2 z-50 border border-gray-100 backdrop-blur-sm">
                          <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50 rounded-t-xl">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-md ring-2 ring-white">
                                <span className="text-white text-xs font-semibold">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">{user.name}</div>
                                <div className="text-xs text-gray-500 flex items-center">
                                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></span>
                                  Online
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mt-1 truncate">{user.email}</div>
                          </div>
                          
                          <div className="py-1">
                            <Link
                              to="/profile"
                              className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group"
                              onClick={() => setIsProfileDropdownOpen(false)}
                            >
                              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-100 transition-colors">
                                <svg className="w-4 h-4 text-gray-600 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-medium">My Profile</div>
                                <div className="text-xs text-gray-500">Account settings</div>
                              </div>
                            </Link>
                            
                            <Link
                              to="/orders"
                              className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group"
                              onClick={() => setIsProfileDropdownOpen(false)}
                            >
                              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-green-100 transition-colors">
                                <svg className="w-4 h-4 text-gray-600 group-hover:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-medium">My Orders</div>
                                <div className="text-xs text-gray-500">Order history</div>
                              </div>
                            </Link>
                          </div>
                          
                          {user.roles && user.roles.some((role: any) => ['admin', 'super_admin'].includes(role.name)) && (
                            <>
                              <div className="border-t border-gray-100 my-1"></div>
                              <Link
                                to="/admin/dashboard"
                                className="flex items-center px-4 py-2.5 text-sm text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group"
                                onClick={() => setIsProfileDropdownOpen(false)}
                              >
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-200 transition-colors">
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </div>
                                <div>
                                  <div className="font-medium">Admin Panel</div>
                                  <div className="text-xs text-blue-500">Management dashboard</div>
                                </div>
                              </Link>
                            </>
                          )}
                          
                          <div className="border-t border-gray-100 my-1"></div>
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsProfileDropdownOpen(false);
                            }}
                            className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 group rounded-b-xl"
                          >
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-red-200 transition-colors">
                              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium">Logout</div>
                              <div className="text-xs text-red-500">Sign out of account</div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Link
                        to="/login"
                        className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        className="text-sm bg-gray-900 text-white px-3 py-1 rounded-md hover:bg-gray-800 font-medium"
                      >
                        Register
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden text-gray-700 hover:text-primary-600 transition-colors"
              >
                {isMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Backdrop */}
          {isMenuOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-300 ease-in-out"
              onClick={() => setIsMenuOpen(false)}
            />
          )}

          {/* Mobile Navigation Drawer */}
          <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}>
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo/logo.PNG" 
                  alt="Signature" 
                  className="h-8 w-auto"
                />
                {/* <span className="text-lg font-semibold text-gray-900">Menu</span> */}
              </div>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors duration-200"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {/* Mobile Navigation Content */}
            <div className="flex flex-col h-[calc(100%-73px)]">
              {/* User Profile Section */}
              {!isLoading && user && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full overflow-hidden shadow-lg ring-3 ring-white">
                        {user.avatar_url ? (
                          <img
                            src={getAvatarUrl(user.avatar_url)}
                            alt={user.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                            <span className="text-white text-lg font-bold tracking-wide">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-semibold text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-600 flex items-center mt-1">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        Online
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">{user.email}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Links */}
              <div className="flex-1 py-4 overflow-y-auto">
                <nav className="space-y-1 px-4">
                  {/* Home */}
                  <Link
                    to="/"
                    className={`flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                      isActiveRoute('/') 
                        ? 'bg-gray-600 text-white shadow-lg' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Home
                  </Link>

                  {/* Products with All Products Link */}
                  <div className="space-y-1">
                    <Link
                      to="/product-signature"
                      className={`flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                        isActiveRoute('/product-signature') 
                          ? 'bg-gray-600 text-white shadow-lg' 
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      All Products
                    </Link>
                    <CategoryNavigation 
                      isMobile={true} 
                      onLinkClick={() => setIsMenuOpen(false)} 
                    />
                  </div>

                  {/* Collections */}
                  <CollectionNavigation 
                    isMobile={true} 
                    onLinkClick={() => setIsMenuOpen(false)} 
                  />

                  {/* Other Navigation Items */}
                  <Link
                    to="/wholesale-registration"
                    className={`flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                      isActiveRoute('/wholesale-registration') 
                        ? 'bg-gray-600 text-white shadow-lg' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Wholesale For Shops
                  </Link>

                  <Link
                    to="/exchange"
                    className={`flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                      isActiveRoute('/exchange') 
                        ? 'bg-gray-600 text-white shadow-lg' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V9a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2z" />
                    </svg>
                    Exchange Request
                  </Link>

                  <Link
                    to="/contact"
                    className={`flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                      isActiveRoute('/contact') 
                        ? 'bg-gray-600 text-white shadow-lg' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                    Contact
                  </Link>
                </nav>

                {/* User Account Section */}
                {!isLoading && user && (
                  <div className="mt-6 px-4">
                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Account</h3>
                      <div className="space-y-1">
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          My Profile
                        </Link>
                        
                        <Link
                          to="/orders"
                          className="flex items-center px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m4.5 4.5V12a1.125 1.125 0 011.125-1.125h2.25m-4.5 7.5V21a.75.75 0 00.75.75h3a.75.75 0 00.75-.75v-1.5m-3 0h3m-3 0h-.375a1.125 1.125 0 01-1.125-1.125V18m3-7.5V12a1.125 1.125 0 011.125-1.125h2.25m-4.5 7.5h4.5a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                          </svg>
                          My Orders
                        </Link>
                        
                        {user.roles && user.roles.some((role: any) => ['admin', 'super_admin'].includes(role.name)) && (
                          <Link
                            to="/admin/dashboard"
                            className="flex items-center px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all duration-200"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
                            </svg>
                            Admin Panel
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Section */}
              <div className="border-t border-gray-200 p-4 bg-white mt-auto">
                {!isLoading && (
                  <div>
                    {user ? (
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-base font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 rounded-xl transition-all duration-200"
                      >
                        <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                        </svg>
                        Logout
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <Link
                          to="/login"
                          className="flex items-center justify-center w-full px-4 py-3 text-base font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Login
                        </Link>
                        <Link
                          to="/register"
                          className="flex items-center justify-center w-full px-4 py-3 text-base font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-all duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Register
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen pt-20 px-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setIsSearchOpen(false)}
            ></div>
            
            {/* Modal */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Search Products</h3>
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    navigate(`/product-signature?search=${encodeURIComponent(searchQuery.trim())}`);
                    setIsSearchOpen(false);
                    setSearchQuery('');
                  }
                }}
                className="relative"
              >
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for products..."
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 transition-all duration-300 focus:outline-none focus:border-gray-900 focus:ring-4 focus:ring-gray-100 hover:border-gray-400"
                  autoFocus
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white p-2 rounded-lg hover:bg-gray-800 active:bg-black transition-all duration-200 hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              </form>
              
              <p className="mt-3 text-sm text-gray-500">
                Press Enter to search or click the search icon
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
